import { Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';

import { PartnerHelper } from '../../../../../model.module/helpers/partner.helper';
import { ConfirmationObservable, MessageObservable } from '../../../../../services.module/message.observable';
import { AppConstants } from '../../../../../app-constants.service';
import { ConfirmModel, MessageModel } from '../../../../../model.module/modals.model';
import { ValidationError } from '../../../../../model.module/validation/validation.error';
import { AuthService } from '../../../../../services.module/auth.service';
import { HttpClient } from '@angular/common/http';
import { PartnerService } from '../../../../../providers.module/partner.service';
import {
    AnschriftAllgemein,
    PartnerProfilData,
    TypedEntityString
} from '../../../../../../generated/api/kreditportalBackendAPI.schemas';
import { PartnerChangeRequest } from '../../../../../model.module/partner-change.model';
import { TypedChangingEntityFactory } from '../../../../../model.module/typed-changing-entity-factory';
import { Subscription, take } from 'rxjs';

@Component({
    selector: 'basisdaten',
    templateUrl: './basisdaten.component.html',
    styleUrls: ['../../../../commonless/content.less', '../../../../commonless/meine-daten.less'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BasisDatenComponent implements OnDestroy {

    modify: string = null;
    validationErrors: ValidationError[] = [];

    partnerModel: PartnerProfilData = null;

    iAmSpy: boolean;
    iAmSteuerberater: boolean;

    changeProfilPartnerRequest: PartnerChangeRequest = null;

    private getPartnerSub: Subscription;

    constructor(
        private readonly http: HttpClient,
        private readonly appConstants: AppConstants,
        private readonly confirmationHandler: ConfirmationObservable,
        private readonly messageHandler: MessageObservable,
        private readonly authService: AuthService,
        private readonly partnerService: PartnerService
    ) {
        this.iAmSpy = this.authService.isMaklerOrVermittler();
        this.iAmSteuerberater = this.authService.isSteuerberater();

        this.getPartnerSub = this.partnerService.getPartner().subscribe(partner => {
            this.partnerModel = partner;
            this.mapPartnerModel();
        });
    }

    ngOnDestroy(): void {
        if (this.getPartnerSub) {
            this.getPartnerSub.unsubscribe();
        }
    }

    private readonly mapPartnerModel = (): void => {
        // 6 Adresszeilen mit blank vorinitialisieren
        const adresszeilen = ['', '', '', ''];

        const anschriftAllgemein: AnschriftAllgemein = this.partnerModel.anschriftAllgemein.value;

        if (PartnerHelper.isAnschriftHaus(anschriftAllgemein.anschriftType)) {
            adresszeilen[0] = anschriftAllgemein.strasse;
            adresszeilen[1] = `${anschriftAllgemein.plz} ${anschriftAllgemein.ort}`;
            adresszeilen[2] = anschriftAllgemein.ortZusatz;
        } else if (PartnerHelper.isAnschriftPostfach(anschriftAllgemein.anschriftType)) {
            adresszeilen[0] = anschriftAllgemein.postfach;
            adresszeilen[1] = `${anschriftAllgemein.plz} ${anschriftAllgemein.ort}`;
            adresszeilen[2] = anschriftAllgemein.ortZusatz;
        } else if (PartnerHelper.isAnschriftGrosskunde(anschriftAllgemein.anschriftType)) {
            adresszeilen[0] = `${anschriftAllgemein.plz} ${anschriftAllgemein.ort}`;
            adresszeilen[1] = anschriftAllgemein.ortZusatz;
        } else if (PartnerHelper.isAnschriftAuslandStrukturiert(anschriftAllgemein.anschriftType)) {
            adresszeilen[0] = anschriftAllgemein.strasse;
            adresszeilen[1] = `${anschriftAllgemein.plz} ${anschriftAllgemein.ort}`;
            adresszeilen[2] = anschriftAllgemein.ortZusatz;
            adresszeilen[3] = anschriftAllgemein.nationText;
        } else if (PartnerHelper.isAnschriftAusland(anschriftAllgemein.anschriftType)) {
            adresszeilen[0] = anschriftAllgemein.zeile1;
            adresszeilen[1] = anschriftAllgemein.zeile2;
            adresszeilen[2] = anschriftAllgemein.zeile3;
            adresszeilen[3] = anschriftAllgemein.zeile4;
        }

        // ODOT geht das noch schöner?
        this.changeProfilPartnerRequest = new PartnerChangeRequest(
            this.partnerModel.externePartnerId.value,
            this.authService.getActiveKunde().vsnr,
            TypedChangingEntityFactory.createByValue(this.getValueOrDefault(this.partnerModel.vornameOderName2)),
            TypedChangingEntityFactory.createByValue(this.getValueOrDefault(this.partnerModel.nachnameOderName1)),
            TypedChangingEntityFactory.createByValue(adresszeilen[0]),
            TypedChangingEntityFactory.createByValue(adresszeilen[1]),
            TypedChangingEntityFactory.createByValue(adresszeilen[2]),
            TypedChangingEntityFactory.createByValue(adresszeilen[3]),
            TypedChangingEntityFactory.createByValue(this.getValueOrDefault(this.partnerModel.email)),
            TypedChangingEntityFactory.createByValue(this.getValueOrDefault(this.partnerModel.telefonnummer)),
            '',
        );
    };


    getValueOrDefault(entity: TypedEntityString): string {
        if (entity === null || entity.value === null) {
            return '';
        }

        return entity.value;
    }

    validateEmail(): void {
        if (!this.changeProfilPartnerRequest.email.recentValue) {
            return;
        }

        this.validationErrors = this.changeProfilPartnerRequest.validate();

        if (!this.validationErrors) {
            this.validationErrors = [];
        }
    }

    validationContains(field: string): boolean {
        return this.validationErrors.find(ve => ve.target === field) !== undefined;
    }

    validationErrorMessagesFor(field: string): string {
        let result: string = '';
        this.validationErrors.filter(ve => ve.target === field).map(ve => result += (ve.message + '<br />'));
        return result;
    }

    changePartnerData(): void {
        const validationResult = this.changeProfilPartnerRequest.validate();
        const valid = validationResult === null;

        if (!valid) {
            console.debug('Ungültiges Modell bei changePartnerData', validationResult);
            return;
        }

        this.changeProfilPartnerRequest.refreshChangedFlags();

        this.confirmationHandler.notify(new ConfirmModel(
            'Abschicken',
            'Möchten Sie Ihre Basisdaten ändern?',
            [this.appConstants.messages.partnerAenderungDetails1, this.appConstants.messages.partnerAenderungDetails2],
            () => this.getCSSDokument(this.changeProfilPartnerRequest),
            (): void => {}
        ));
    }

    getCSSDokument(request: any): void {
        this.http.get('/assets/css/print4.css', { responseType: 'text' })
            .subscribe({
                next: (data: string) => {
                    this.sendProfildatenAenderung(data, request);
                },
                error: error => {
                    console.error(error.message);
                    this.modify = null;
                }
            });
    }

    private sendProfildatenAenderung(css: string, request: any): void {
        const elemIdentifier = 'printDialogue';
        const printContents = document.getElementById(elemIdentifier).innerHTML;

        request['archivDokument'] = `
          <html lang="de">
              <head>
                  <title>VHV Kreditportal</title>
                  <meta http-equiv="Content-Type" content="text/html;charset=UTF-8">
                  <style>
                      ${css}
                  </style>
              </head>
              <body>
                  ${printContents}
              </body>
          </html>`;

        this.partnerService.changeProfilData(request)
            .pipe(take(1))
            .subscribe(() => {
                this.messageHandler.notify(new MessageModel(
                    this.appConstants.messages.partnerAenderungSuccess,
                    [this.appConstants.messages.partnerAenderungDetails1, this.appConstants.messages.partnerAenderungDetails2],
                ));
                console.debug('Partnerdaten-Änderung wurde erfolgreich beauftragt', this);
                this.modify = null;
            });
    }

}
